import { useCallback, useEffect, useState } from 'react';
import QualificationCallBooking from '../components/tenant/QualificationCallBooking';
import QualificationLandingVideoSection from '../components/tenant/QualificationLandingVideoSection';
import { VIDEO_SALES_LETTER_SECTION } from '../constants/liveWithUsPortalContent';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { discoveryCallProgress } from '../utils/prospect-sidebar-progress';
import type { PortalContentEngagementRollup } from '../services/tenantApi';

const DISCOVERY_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-none';

const HEADER_CARD =
  'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5';

export default function DiscoveryCallPage() {
  const setPageProgress = useSetPageProgress();
  const [videoDone, setVideoDone] = useState<boolean | null>(null);
  const [callShowed, setCallShowed] = useState<boolean | null>(null);
  const [canBook, setCanBook] = useState<boolean | null>(null);

  const onVideoEngagement = useCallback((engagement: PortalContentEngagementRollup | null) => {
    setVideoDone(engagement?.watchedHalf === true);
  }, []);

  const onCallAccess = useCallback((access: { callShowed: boolean; canBook: boolean }) => {
    setCallShowed(access.callShowed);
    setCanBook(access.canBook);
  }, []);

  useEffect(() => {
    if (videoDone === null || callShowed === null) return;
    setPageProgress('/discovery-call', discoveryCallProgress(videoDone, callShowed));
  }, [callShowed, setPageProgress, videoDone]);

  return (
    <div className={`${DISCOVERY_SHELL} flex flex-col gap-4 pb-2 sm:pb-4`}>
      <header className={`shrink-0 ${HEADER_CARD}`}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 2</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">Discovery Call</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          {canBook === false
            ? 'The discovery call is for the primary contact. You can still watch how leasing with us works.'
            : 'Watch how leasing with us works, then book a call about finding your home. Only the primary contact books the call.'}
        </p>
      </header>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 lg:items-stretch">
        <div className="min-w-0 lg:flex lg:min-h-0 lg:flex-col">
            <QualificationLandingVideoSection
              centerInCard
              className="mb-0 w-full lg:min-h-0 lg:flex-1"
              headerClassName="border-b border-gray-100 px-4 py-4 dark:border-white/5 sm:px-5"
              bodyClassName="px-4 py-4 sm:px-5"
              titleClassName="text-base font-semibold leading-snug text-gray-900 dark:text-white"
              descriptionClassName="mt-1 text-sm text-gray-500 dark:text-gray-400"
              sectionName={VIDEO_SALES_LETTER_SECTION}
              minuteStep
              onEngagement={onVideoEngagement}
              title="How Leasing Works With Us"
              description={
                canBook === false
                  ? 'A look at how leasing with us works. Booking the call is for the primary contact.'
                  : '⚠️ Please watch this video. It is a prerequisite for the discovery call. ⚠️'
              }
              placeholderLabel="How leasing with us works"
            />
        </div>
        <QualificationCallBooking onAccessChange={onCallAccess} videoWatched={videoDone === true} />
      </div>
    </div>
  );
}
